/**
 * Lexical DecoratorNode for Secret Fields
 */

import React from 'react';
import {
  DecoratorNode,
  EditorConfig,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
  DOMExportOutput,
  DOMConversionMap,
  DOMConversionOutput,
  LexicalEditor,
  BLUR_COMMAND,
} from 'lexical';
import { SecretFieldNodeJSON, SecretFieldType } from './types';
import SecretFieldEditor from './SecretFieldEditor';
import SecretFieldView from './SecretFieldView';

export type SerializedSecretFieldNode = Spread<
  SecretFieldNodeJSON,
  SerializedLexicalNode
>;

/**
 * SecretFieldNode - Custom Lexical node for secret fields
 */
export class SecretFieldNode extends DecoratorNode<React.ReactElement> {
  __label: string;
  __content: string;
  __fieldType: SecretFieldType;

  static getType(): string {
    return 'secretField';
  }

  static clone(node: SecretFieldNode): SecretFieldNode {
    return new SecretFieldNode(
      node.__label,
      node.__content,
      node.__fieldType,
      node.__key
    );
  }

  constructor(
    label: string,
    content: string,
    fieldType: SecretFieldType = 'inline',
    key?: NodeKey
  ) {
    super(key);
    this.__label = label;
    this.__content = content;
    this.__fieldType = fieldType;
  }

  // Getters
  getLabel(): string {
    return this.__label;
  }

  getContent(): string {
    return this.__content;
  }

  getFieldType(): SecretFieldType {
    return this.__fieldType;
  }

  // Setters
  setLabel(label: string): void {
    const writable = this.getWritable();
    writable.__label = label;
  }

  setContent(content: string): void {
    const writable = this.getWritable();
    writable.__content = content;
  }

  setFieldType(fieldType: SecretFieldType): void {
    const writable = this.getWritable();
    writable.__fieldType = fieldType;
  }

  // Prevent whitespace fixing errors in export
  getTextContent(): string {
    return '';
  }

  // DecoratorNodes don't have children
  getChildren(): never[] {
    return [];
  }

  // Create DOM element
  createDOM(config: EditorConfig): HTMLElement {
    const element = document.createElement(this.__fieldType === 'inline' ? 'span' : 'div');
    element.className = `secret-field secret-field-${this.__fieldType}`;
    return element;
  }

  // Update DOM element
  updateDOM(
    prevNode: SecretFieldNode,
    dom: HTMLElement,
    config: EditorConfig
  ): boolean {
    // Return true if DOM needs to be recreated
    return prevNode.__fieldType !== this.__fieldType;
  }

  // Export to JSON
  exportJSON(): SerializedSecretFieldNode {
    return {
      type: 'secretField',
      label: this.__label,
      content: this.__content,
      fieldType: this.__fieldType,
      version: 1,
    };
  }

  // Import from JSON
  static importJSON(serializedNode: SerializedSecretFieldNode): SecretFieldNode {
    const node = $createSecretFieldNode(
      serializedNode.label,
      serializedNode.content,
      serializedNode.fieldType
    );
    return node;
  }

  // Export to DOM (for copy/paste)
  exportDOM(): DOMExportOutput {
    const element = document.createElement(this.__fieldType === 'inline' ? 'span' : 'div');
    element.setAttribute('data-secret-field', 'true');
    element.setAttribute('data-label', this.__label);
    element.setAttribute('data-type', this.__fieldType);
    element.textContent = '🔒 ' + this.__label;
    return { element };
  }

  // Import from DOM (for paste)
  static importDOM(): DOMConversionMap | null {
    return {
      div: (domNode: HTMLElement) => {
        if (domNode.hasAttribute('data-secret-field')) {
          return {
            conversion: convertSecretFieldElement,
            priority: 1,
          };
        }
        return null;
      },
      span: (domNode: HTMLElement) => {
        if (domNode.hasAttribute('data-secret-field')) {
          return {
            conversion: convertSecretFieldElement,
            priority: 1,
          };
        }
        return null;
      },
    };
  }

  // Render the React component
  decorate(editor: LexicalEditor, config: EditorConfig): React.ReactElement {
    // Check if editor is in read-only mode
    const isReadOnly = !editor.isEditable();

    // Helper to dispatch blur command for immediate persistence (table cells)
    const dispatchBlurToSave = () => {
      const blurEvent = new FocusEvent('blur', {
        bubbles: true,
        cancelable: true,
        relatedTarget: document.body, // Focus moved outside editor
      });
      editor.dispatchCommand(BLUR_COMMAND, blurEvent);
    };

    // Render appropriate component based on mode
    if (isReadOnly) {
      return (
        <SecretFieldView
          nodeKey={this.getKey()}
          label={this.__label}
          content={this.__content}
          type={this.__fieldType}
          isRevealed={false}
          onToggle={() => {
            // Toggle logic will be handled by context in Phase 4
            console.log('Toggle secret field visibility');
          }}
        />
      );
    } else {
      return (
        <SecretFieldEditor
          nodeKey={this.getKey()}
          label={this.__label}
          content={this.__content}
          type={this.__fieldType}
          onUpdate={(newLabel: string, newContent: string) => {
            editor.update(() => {
              const writable = this.getWritable();
              writable.__label = newLabel;
              writable.__content = newContent;
            });
            // Trigger save immediately after update
            dispatchBlurToSave();
          }}
          onCommit={() => {
            // Trigger save when editing is committed (blur)
            dispatchBlurToSave();
          }}
          onTypeChange={(newType: SecretFieldType) => {
            editor.update(() => {
              const writable = this.getWritable();
              writable.__fieldType = newType;
            });
            // Trigger save immediately after type change
            dispatchBlurToSave();
          }}
          onDelete={() => {
            editor.update(() => {
              this.remove();
            });
            // Trigger save immediately after delete
            dispatchBlurToSave();
          }}
        />
      );
    }
  }

  // Always inline
  isInline(): boolean {
    return true;
  }

  // Prevent text selection inside
  isIsolated(): boolean {
    return true;
  }

  // Make it keyboard selectable
  isKeyboardSelectable(): boolean {
    return true;
  }
}

/**
 * Helper function to create a SecretFieldNode
 */
export function $createSecretFieldNode(
  label: string,
  content: string,
  fieldType: SecretFieldType = 'inline'
): SecretFieldNode {
  return new SecretFieldNode(label, content, fieldType);
}

/**
 * Type guard to check if a node is a SecretFieldNode
 */
export function $isSecretFieldNode(
  node: LexicalNode | null | undefined
): node is SecretFieldNode {
  return node instanceof SecretFieldNode;
}

/**
 * Convert DOM element to SecretFieldNode
 */
function convertSecretFieldElement(domNode: HTMLElement): DOMConversionOutput | null {
  const label = domNode.getAttribute('data-label');
  const type = domNode.getAttribute('data-type') as SecretFieldType;

  if (!label) {
    return null;
  }

  const node = $createSecretFieldNode(label, '', type || 'inline');
  return { node };
}

